/* eslint-disable @typescript-eslint/no-magic-numbers */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, output, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { EnhancePatientNotePromptType } from '@ffd/api-client';
import { AudioInputDropdownComponent, VolumeLevelService } from '@libs/shared-services';
import { DeepGramSpeechRecognitionService } from '@libs/speech-recognition';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';

import { ContextMenuHeaderButton, GenerateTextAction, TooltipModule, VoiceTypingTextFieldComponent } from '@libs/ui-lib';
import { NoteSavingIndicatorComponent } from '@libs/notes';
import { TextareaWithAIService } from './textarea-with-ai.service';
import { EnhanceButtonsComponent } from './components/enhance-buttons';

@Component({
  selector: 'lib-textarea-with-ai',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    LottieComponent,
    AudioInputDropdownComponent,
    VoiceTypingTextFieldComponent,
    TooltipModule,
    NoteSavingIndicatorComponent,
    EnhanceButtonsComponent,
  ],
  templateUrl: './textarea-with-ai.component.html',
  styleUrl: './textarea-with-ai.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TextareaWithAIService,
    DeepGramSpeechRecognitionService,
    VolumeLevelService,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TextareaWithAIComponent,
    },
  ],
})
export class TextareaWithAIComponent implements OnDestroy, ControlValueAccessor {
  private readonly textareaWithAIService = inject(TextareaWithAIService);
  private readonly speechRecognitionService = inject(DeepGramSpeechRecognitionService);

  public readonly maxRows = input<number | undefined>();
  public readonly isInputFocused = input<boolean>(false);
  public readonly autoSave = input<boolean>();
  public readonly errorState = input<boolean>(false);
  public readonly closeButtonDisabled = input<boolean>(false);
  public readonly enableVoiceTyping = input<boolean>(true);
  public readonly isSaving = input<boolean>(false);
  public readonly isSaved = input<boolean>(false);
  public readonly contextMenuHeaderConfig = input<ContextMenuHeaderButton[]>([
    {
      text: 'health_history_notes_section_rewrite_enhance',
      type: GenerateTextAction.HealthHistoryRewrite,
    },
    {
      text: 'health_history_notes_section_concise_enhance',
      type: GenerateTextAction.HealthHistoryConcise,
    },
    {
      text: 'health_history_notes_section_bullets_enhance',
      type: GenerateTextAction.HealthHistoryBullets,
    },
  ]);

  public readonly noteClicked = output();
  public readonly closeClicked = output();
  public readonly voiceTypingStatusChange = output<boolean>();

  public readonly voiceTypingTextField = viewChild<VoiceTypingTextFieldComponent>('voiceTypingTextField');

  public controlledNote$ = this.textareaWithAIService.controlledNote$;
  public undoDisabledSignal = this.textareaWithAIService.undoDisabledSignal;
  public redoDisabledSignal = this.textareaWithAIService.redoDisabledSignal;
  public isGeneratingSignal = this.textareaWithAIService.isGeneratingSignal;
  public currentHistoryElementSignal = this.textareaWithAIService.currentHistoryElementSignal;
  public lastFocusedCursorOffsetSignal = this.textareaWithAIService.lastFocusedCursorOffsetSignal;
  public readonly transcription = this.speechRecognitionService.transcription;
  public readonly transcriptionStatus = this.speechRecognitionService.status;
  public readonly transcriptionStatus$ = toObservable(this.speechRecognitionService.status);
  public readonly activeDeviceId = this.speechRecognitionService.activeDeviceId;

  public readonly textFieldMaxHeightStyle = computed(() => {
    if (this.maxRows()) {
      const rowHeight = 24; // This should match the line-height in CSS
      const maxHeight = this.maxRows()! * rowHeight;
      return { 'max-height': `${maxHeight}px` };
    }

    return {};
  });

  protected isMicSettingsOpen = signal(false);

  protected loadingLottieOptions: AnimationOptions = {
    path: 'assets/animations/loading-spinner.json',
  };

  protected readonly touched = signal<boolean>(false);
  protected readonly disabled = signal<boolean>(false);
  private readonly isInitialized = signal<boolean>(false);
  private initialValue: string | null = null;

  private onChange = (value: string) => {};
  private onTouched = () => {};

  public writeValue(value: string): void {
    if (!this.isInitialized()) {
      this.initialValue = value;
      this.isInitialized.set(true);
    }

    this.textareaWithAIService.setControlledNote(value);
    if (this.isInitialized() && value !== this.initialValue) {
      this.textareaWithAIService.noteTyping({ notes: value, autoSave: this.autoSave() });
    }
  }

  public registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public markAsTouched(): void {
    if (!this.touched()) {
      this.onTouched();
      this.touched.set(true);
    }
  }

  public setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  constructor() {
    effect(() => {
      const savedNote = this.textareaWithAIService.savedNoteSignal();
      if (savedNote === null || !this.isInitialized()) {
        return;
      }

      if (savedNote !== this.initialValue) {
        this.markAsTouched();
        this.onChange(savedNote);
      }
    });
  }

  public updateLastFocusedCursorOffset(offset: number): void {
    this.textareaWithAIService.updateLastFocusedCursorOffset(offset);
  }

  public ngOnDestroy(): void {
    if (this.isGeneratingSignal()) {
      this.textareaWithAIService.abortEnhance();
    }
    if (this.transcriptionStatus() === 'recording' || this.transcriptionStatus() === 'loading') {
      this.speechRecognitionService.stopRecording();
    }
    this.textareaWithAIService.resetHistory();
  }

  public onDictationClick(): void {
    this.textareaWithAIService.toggleSpeechRecognitionListening();
    if (this.transcriptionStatus() === 'recording' || this.transcriptionStatus() === 'loading') {
      this.voiceTypingStatusChange.emit(true);
    } else {
      this.voiceTypingStatusChange.emit(false);
    }
  }

  public enhanceNote(enhanceType: EnhancePatientNotePromptType): void {
    this.textareaWithAIService.enhanceNote({
      noteInput: this.voiceTypingTextField()?.getTemplateText() ?? '',
      enhanceType,
    });
  }

  public onRecordingLoading(note: string): void {
    this.textareaWithAIService.setControlledNote(note);
  }

  public redo(): void {
    this.textareaWithAIService.redo();
    this.voiceTypingTextField()?.regenerate();
  }

  public undo(): void {
    this.textareaWithAIService.undo();
    this.voiceTypingTextField()?.regenerate();
  }

  public onManualTypeKeyUp(text: string): void {
    this.textareaWithAIService.noteTyping({ notes: text, autoSave: this.autoSave() });
  }

  public cancelEdit(): void {
    this.textareaWithAIService.resetHistory();
  }

  protected onSelectDevice(deviceId: string): void {
    this.textareaWithAIService.selectMicrophone(deviceId);
  }

  protected onCloseClick(): void {
    this.closeClicked.emit();
  }

  public onGenerateTextFromContextMenu({
    action,
    text,
    textStart,
    textEnd,
  }: {
    action: GenerateTextAction;
    text: string;
    textStart: number;
    textEnd: number;
  }): void {
    this.textareaWithAIService.enhanceNote({
      noteInput: text,
      enhanceType: this.mapEnhancePromptType(action),
      textStart,
      textEnd,
      fullNote: this.voiceTypingTextField()?.getTemplateText() ?? '',
    });
  }

  private mapEnhancePromptType(action: GenerateTextAction): EnhancePatientNotePromptType {
    switch (action) {
      case GenerateTextAction.HealthHistoryBullets: {
        return EnhancePatientNotePromptType.BULLETS;
      }
      case GenerateTextAction.HealthHistoryConcise: {
        return EnhancePatientNotePromptType.CONCISE;
      }
      case GenerateTextAction.HealthHistoryRewrite: {
        return EnhancePatientNotePromptType.REWRITE;
      }
      default: {
        return EnhancePatientNotePromptType.REWRITE;
      }
    }
  }
}
